import { usePage, router, Link } from "@inertiajs/react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { AppLayout } from "@/components/layouts/AppLayout"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { SortableTableHead } from "@/components/ui/sortable-table-head"
import { FilterCombobox } from "@/components/FilterCombobox"
import { FlashMessages } from "@/components/FlashMessages"
import { FileText, AlertTriangle, ClipboardList } from "lucide-react"
import { assetTypeLabel } from "@/lib/assetTypes"
import type { AssetType } from "@/lib/assetTypes"
import type { AppPageProps } from "@/types"

type ContractSort = "code" | "last_event_at"

interface ContractAsset {
  id: string
  code: string
  assetable_type: AssetType
  last_event_at: string | null
}

interface ContractAccount {
  name: string
  tax_name: string | null
  tax_id: string | null
}

interface ContractNewPageProps extends AppPageProps {
  customers: { id: string; name: string }[]
  customerAssets: ContractAsset[]
  selectedCustomerId: string | null
  sort: ContractSort
  direction: "asc" | "desc"
  account: ContractAccount
}

function formatDateTime(iso: string | null, locale: string): string {
  if (!iso) return "—"
  return new Date(iso).toLocaleDateString(locale, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}

export default function ContractNew() {
  const { t, i18n } = useTranslation()
  const { auth, flash, customers, customerAssets, selectedCustomerId, sort, direction, account } =
    usePage<ContractNewPageProps>().props

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())

  const missingInfo = !account.tax_name || !account.tax_id

  function handleCustomerChange(customerId: string) {
    setSelectedIds(new Set())
    router.get(
      "/contracts/new",
      customerId ? { customer_id: customerId } : {},
      { preserveState: true }
    )
  }

  function handleSort(column: string) {
    const newDirection = sort === column && direction === "asc" ? "desc" : "asc"
    router.get(
      "/contracts/new",
      {
        customer_id: selectedCustomerId || undefined,
        sort: column,
        direction: newDirection,
      },
      { preserveState: true, preserveScroll: true }
    )
  }

  function handleToggle(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  function handleSelectAll() {
    if (selectedIds.size === customerAssets.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(customerAssets.map((a) => a.id)))
    }
  }

  function handleGenerate() {
    const params = new URLSearchParams()
    params.set("customer_id", selectedCustomerId!)
    selectedIds.forEach((id) => params.append("asset_ids[]", id))
    router.get(`/contracts/preview?${params.toString()}`)
  }

  function handleGenerateOrder() {
    const params = new URLSearchParams()
    params.set("customer_id", selectedCustomerId!)
    selectedIds.forEach((id) => params.append("asset_ids[]", id))
    router.get(`/contracts/order?${params.toString()}`)
  }

  const customerOptions = customers.map((c) => ({ id: c.id, name: c.name }))
  const selectedCustomer = customers.find((c) => c.id === selectedCustomerId)

  return (
    <AppLayout
      user={auth.user}
      accounts={auth.accounts}
      currentAccountId={auth.currentAccountId}
      role={auth.role}
    >
      <FlashMessages notice={flash.notice} alert={flash.alert} />

      <div className="space-y-6">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">{t("contracts.title")}</h2>
          <p className="text-muted-foreground">{t("contracts.subtitle")}</p>
        </div>

        {missingInfo && (
          <div className="flex items-center gap-3 rounded-lg border border-yellow-300 bg-yellow-50 p-4 text-sm text-yellow-800 dark:border-yellow-700 dark:bg-yellow-950 dark:text-yellow-200">
            <AlertTriangle className="h-5 w-5 shrink-0" />
            <span>{t("contracts.missingAccountInfo")}</span>
            <Link href="/settings" className="ml-auto font-medium underline whitespace-nowrap">
              {t("contracts.goToSettings")}
            </Link>
          </div>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              {t("contracts.title")}
            </CardTitle>
            <CardDescription>{t("contracts.subtitle")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("contracts.customerLabel")}</label>
              <FilterCombobox
                value={selectedCustomerId || ""}
                onChange={handleCustomerChange}
                options={customerOptions}
                placeholder={t("contracts.selectCustomer")}
                emptyLabel={t("contracts.selectCustomer")}
                className="w-full"
              />
            </div>

            {selectedCustomerId && (
              <>
                {customerAssets.length === 0 ? (
                  <p className="text-sm text-muted-foreground">{t("contracts.noAssets")}</p>
                ) : (
                  <>
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-medium">
                        {t("contracts.assetsForCustomer", { name: selectedCustomer?.name })}
                      </h3>
                      {selectedIds.size > 0 && (
                        <span className="text-sm text-muted-foreground">
                          {t("contracts.selected", { count: selectedIds.size })}
                        </span>
                      )}
                    </div>

                    <div className="rounded-md border">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-12">
                              <Checkbox
                                checked={
                                  customerAssets.length > 0 &&
                                  selectedIds.size === customerAssets.length
                                }
                                onCheckedChange={handleSelectAll}
                                aria-label={t("contracts.selectAll")}
                              />
                            </TableHead>
                            <TableHead className="w-12">#</TableHead>
                            <SortableTableHead
                              label={t("contracts.document.tableCode")}
                              sortKey="code"
                              currentSortKey={sort}
                              direction={direction}
                              onSort={handleSort}
                            />
                            <TableHead>{t("contracts.document.tableType")}</TableHead>
                            <SortableTableHead
                              label={t("inventory.table.lastEvent")}
                              sortKey="last_event_at"
                              currentSortKey={sort}
                              direction={direction}
                              onSort={handleSort}
                              className="w-48"
                            />
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {customerAssets.map((asset, index) => (
                            <TableRow key={asset.id}>
                              <TableCell>
                                <Checkbox
                                  checked={selectedIds.has(asset.id)}
                                  onCheckedChange={() => handleToggle(asset.id)}
                                />
                              </TableCell>
                              <TableCell className="text-muted-foreground">
                                {index + 1}
                              </TableCell>
                              <TableCell className="font-medium">{asset.code}</TableCell>
                              <TableCell>
                                {assetTypeLabel(asset.assetable_type, t)}
                              </TableCell>
                              <TableCell className="text-muted-foreground text-sm">
                                {formatDateTime(asset.last_event_at, i18n.language)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>

                    <div className="flex gap-3">
                      <Button
                        onClick={handleGenerate}
                        disabled={selectedIds.size === 0 || missingInfo}
                      >
                        <FileText className="w-4 h-4 mr-2" />
                        {t("contracts.generateButton")}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={handleGenerateOrder}
                        disabled={selectedIds.size === 0 || missingInfo}
                      >
                        <ClipboardList className="w-4 h-4 mr-2" />
                        {t("contracts.generateOrder")}
                      </Button>
                    </div>
                  </>
                )}
              </>
            )}

            {!selectedCustomerId && customers.length === 0 && (
              <p className="text-sm text-muted-foreground">{t("contracts.noCustomersWithAssets")}</p>
            )}
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  )
}
